import { serverTimestamp, type Timestamp } from "firebase/firestore";
import { action, computed, makeObservable, observable } from "mobx";
import { TKey } from "../../shared/translations/translationKeys";
import { PlaniraLogType } from "../../shared/types";
import type { ModuleDefaultData } from "../../templates/generator/types";
import type {
  ModuleCompleteInfo,
  ModuleDoneType,
  ModuleType,
  PlanQuestionType,
} from "../../templates/types";
import { ModuleState } from "../../templates/types";
import { ActivityEventType } from "../activities/types";
import type { Api } from "../core/apis";
import { BaseModel, type BaseModelData } from "../core/BaseModel";
import type { Stores } from "../core/stores";
import { arrayMove } from "../core/utils";
import type BaseModuleModel from "../modules/core/BaseModuleModel";
import type {
  ModuleModelData,
  Snapshot,
} from "../modules/core/BaseModuleModel";

export interface SectionModelData extends BaseModelData {
  planUid: string;
  title: string | null;
  moduleIds: string[];
  attractState?: ModuleState;
}

export type CreateModule = {
  type: ModuleType;
  doneType: ModuleDoneType;
  completeAction: ModuleCompleteInfo | null;
  title: string;
  emoji: string;
  isDraggable: boolean;
  initiated: boolean;
  questionType: PlanQuestionType;
};

export default class SectionModel extends BaseModel<SectionModelData> {
  constructor(stores: Stores, api: Api, data: SectionModelData) {
    super(stores, api, data);

    makeObservable(this);
  }

  @observable showModulesSheet = false;
  @action setShowModulesSheet(show: boolean) {
    this.showModulesSheet = show;
  }

  @computed get isArchivable() {
    return !this.data.moduleIds.length && this.data.isExpandable;
  }

  @computed get isModulesLoaded() {
    return this.data.moduleIds.length === this.modules.length;
  }

  @action async deleteSection() {
    for (const moduleUid of this.data.moduleIds) {
      const module = this.stores.modulesStore.byId.get(moduleUid);
      if (!module) {
        throw Error("Could not delete section");
      }

      await module.deleteModule();
    }

    const plan = this.plan;
    plan.data.sectionIds = plan.data.sectionIds.filter(
      (uid) => uid !== this.uid,
    );
    await this.api.plansApi.set(plan);

    this.api.sectionsApi.delete(this);
  }

  @action async setAsArchived(status = true) {
    this.data.archived = status;
    return this.api.sectionsApi.update(this.uid, {
      archived: this.data.archived,
    });
  }

  @action async updateOrder(moduleIds: string[]) {
    this.data.moduleIds = moduleIds;
    this.api.sectionsApi.update(this.uid, { moduleIds: this.data.moduleIds });
  }

  async removeModule(moduleUid: string) {
    this.data.moduleIds = this.data.moduleIds.filter(
      (mUid) => mUid !== moduleUid,
    );
    await this.api.sectionsApi.set(this);
  }

  async addExistingModule(moduleUid: string) {
    if (!this.data.moduleIds.includes(moduleUid)) {
      this.data.moduleIds.push(moduleUid);
      await this.api.sectionsApi.set(this);
    }
  }

  async addModule<T extends CreateModule>(
    data: T,
    options?: { skipActivity?: boolean; defaultData?: ModuleDefaultData },
  ) {
    const module = await this.api.modulesApi.add({
      ts: serverTimestamp() as Timestamp,
      state: ModuleState.Planning,
      planUid: this.plan.uid,
      businessPlanUid: "",
      feedItemUid: "",
      sectionUid: this.uid,
      lastActivity: serverTimestamp() as Timestamp,
      comments: 0,
      isMuted: false,
      snapshot: {},
      descriptionYooptaEditor: null,
      recentActivity: [],
      ...data,
      ...options?.defaultData,
    });

    // Update snapshot at once
    module.updateSnapshotWithoutSave();
    this.api.modulesApi.set(module);

    this.data.moduleIds.push(module.uid);
    this.api.sectionsApi.set(this);

    if (!options?.skipActivity) {
      module.postActivityEvent(
        ActivityEventType.Added,
        TKey.ActivityModuleAdded,
        {
          label: data.title,
        },
      );

      void this.plan.addPlaniraLogItem(
        PlaniraLogType.CardCreated,
        `Card "${data.emoji} ${data.title}" (${data.type}) was created`,
      );
    }

    return module;
  }

  @action async setTitle(title: string) {
    if (title !== this.data.title) {
      this.data.title = title;
      await this.api.sectionsApi.update(this.uid, { title: this.data.title });
    }
  }

  @action async moveModule(moduleUid: string, direction: "up" | "down") {
    const index = this.data.moduleIds.indexOf(moduleUid);
    if (index !== -1) {
      this.data.moduleIds = arrayMove(
        this.data.moduleIds,
        index,
        direction === "up" ? index - 1 : index + 1,
      );
      await this.api.sectionsApi.update(this.uid, {
        moduleIds: this.data.moduleIds,
      });
    }
  }

  @computed get plan() {
    const plan = this.stores.plansStore.byId.get(this.data.planUid);
    if (!plan) {
      throw Error("No plan found!");
    }
    return plan;
  }

  @computed get modules(): BaseModuleModel<
    Snapshot,
    ModuleModelData<Snapshot>
  >[] {
    const u: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>[] = [];
    for (const moduleId of this.data.moduleIds) {
      const uModel = this.stores.modulesStore.byId.get(moduleId);
      if (uModel) {
        u.push(uModel);
      }
    }

    return u;
  }

  @computed get allModulesAreLoaded(): boolean {
    return this.modules.length === this.data.moduleIds.length;
  }

  toFireStore(): Omit<SectionModelData, "uid"> {
    const data: Omit<SectionModelData, "uid"> = {
      ...this.fromBaseModel(),
      ...this.data,
    };

    return data;
  }
}
